import React, { useCallback, useEffect, useMemo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Grid } from '@atlaskit/primitives/compiled/grid';
import { token } from '@atlaskit/tokens';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import GettingStartedCard from '@atlassian/getting-started-widget/getting-started-widget';

import type { onboardingStateGetOnboardingStateQuery$data } from '@confluence/onboarding-helpers/entry-points/onboardingStateGetOnboardingStateQuery.ts';
import { ActivityFilterDialog } from '@confluence/activity-filter/entry-points/ActivityFilterDialog.ts';
import { NTH_USER_HOME_ONBOARDING_REVAMP_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import { usePersonalizedTemplateOnboardingActions } from '../hooks/usePersonalizedTemplateOnboardingActions.tsx';
import { usePTV2State } from '../usePTV2Store.tsx';
import { useNthUserActions } from '../hooks/useNthUserActions.ts';

import { NTH_USER_ONBOARDING_ACTIONS_DATA } from './cards/NthUserCards.tsx';
import { useActionHandlers, useFeedHandlers } from './hooks/useOnboardingHandlers.ts';

type NthUserHomeOnboardingActionCardsProps = {
	onboardingStateData?: onboardingStateGetOnboardingStateQuery$data;
};

const SOURCE = 'nth-user-home-onboarding-revamp';

const i18n = defineMessages({
	heading: {
		id: 'experiment-personalized-templates-v2.nth-user.action.cards.heading',
		defaultMessage: 'Getting started',
		description:
			'Heading displayed at the top of the action cards panel to introduce a series of onboarding tasks for new users.',
	},
});

const styles = cssMap({
	containerStyles: {
		paddingBottom: token('space.300'),
	},
	headerStyles: {
		alignItems: 'center',
		paddingBottom: token('space.150'),
	},
	gridStyles: {
		gridTemplateColumns: '1fr 1fr',
		'@media (min-width: 64rem)': {
			gridTemplateColumns: '1fr 1fr 1fr 1fr',
		},
	},
});

export const NthUserHomeOnboardingActionCards = ({
	onboardingStateData,
}: NthUserHomeOnboardingActionCardsProps): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { onboardingPeriodDay } = usePTV2State();
	const { actions, dismiss, complete } = usePersonalizedTemplateOnboardingActions(
		NTH_USER_ONBOARDING_ACTIONS_DATA,
		onboardingStateData,
		NTH_USER_HOME_ONBOARDING_REVAMP_ONBOARDING_STATE_KEYS.onboardingActionStates,
	);

	const {
		activityFilterModalOpen,
		closeActivityFilterModal,
		handleActivityFilterModalSave,
		openActivityFilterModal,
	} = useFeedHandlers();
	const { handleActionClick } = useActionHandlers(openActivityFilterModal);

	const handleClick = useCallback(
		(id: string, _event: React.MouseEvent<Element> | React.KeyboardEvent<Element>) => {
			handleActionClick(id, complete);
		},
		[handleActionClick, complete],
	);

	const handleDismiss = useCallback(
		(id: string) => {
			dismiss?.(id);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'dismissGettingStartedCardNthUserHomeRevamp',
					source: SOURCE,
					attributes: {
						onboardingAction: id,
						onboardingPeriodDay,
					},
				},
			}).fire();
		},
		[dismiss, createAnalyticsEvent, onboardingPeriodDay],
	);

	// Filter and sort actions
	const actionsMap = useMemo(
		() => Object.fromEntries(actions.map((action) => [action.id, action])),
		[actions],
	);

	const dismissedCardIds = useMemo(
		() =>
			new Set(
				Object.values(actionsMap)
					.filter((action) => action.state === 'dismissed')
					.map((action) => action.id),
			),
		[actionsMap],
	);

	const { actionCardsToDisplay: filteredOnboardingActionCards } =
		useNthUserActions(dismissedCardIds);

	useEffect(() => {
		if (filteredOnboardingActionCards.length > 0) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'viewed',
					actionSubject: 'homeRevampActionCards',
					source: SOURCE,
					attributes: {
						numberOfCards: filteredOnboardingActionCards.length,
						onboardingPeriodDay,
					},
				},
			}).fire();
		}
	}, [filteredOnboardingActionCards.length, createAnalyticsEvent, onboardingPeriodDay]);

	if (filteredOnboardingActionCards.length === 0) {
		return null;
	}

	const nonCompletedCards = filteredOnboardingActionCards.filter(
		(card) => actionsMap[card.id]?.state !== 'completed',
	);
	const completedCards = filteredOnboardingActionCards.filter(
		(card) => actionsMap[card.id]?.state === 'completed',
	);
	const sortedOnboardingActionCards = [...nonCompletedCards, ...completedCards];

	return (
		<Box xcss={styles.containerStyles}>
			<Flex xcss={styles.headerStyles} justifyContent="space-between">
				<Heading size="xsmall">
					<FormattedMessage {...i18n.heading} />
				</Heading>
			</Flex>
			<Grid xcss={styles.gridStyles} gap="space.200">
				{sortedOnboardingActionCards.map((card) => (
					<GettingStartedCard
						key={card.id}
						image={card.image}
						heading={card.heading}
						description={card.description}
						ctaText={card.ctaText}
						ctaCompletedText={card.ctaCompletedText}
						dismissTooltip={card.dismissTooltip}
						onClick={(event: React.MouseEvent<Element> | React.KeyboardEvent<Element>) =>
							handleClick(card.id, event)
						}
						onDismiss={() => handleDismiss(card.id)}
						cardCompleted={actionsMap[card.id]?.state === 'completed'}
						hasBorder
						headingSize="xsmall"
					/>
				))}
			</Grid>
			<ModalTransition>
				{activityFilterModalOpen && (
					<ActivityFilterDialog
						onChanged={handleActivityFilterModalSave}
						onClose={closeActivityFilterModal}
					/>
				)}
			</ModalTransition>
		</Box>
	);
};
